import React, { useMemo } from 'react';

import { Lock } from 'react-feather';
import { Field, useForm,useFormState } from 'react-final-form';
import { useDispatch,useSelector } from 'react-redux';

import {
  Button,
  ExpandableSection,
  FormDatePicker,
  FormField,
  FormRadio,
  FormSelect,
  FormSwitch,
  FormTooltipIcon,
  TextArea,
  TimeRangeSelector} from 'components';
import { actions as aiActions } from 'coupon-common/src/modules/aiContent';
import { dispatchAsAsync } from 'coupon-common/src/utils';

import { termsPrompt } from '../../coupon-ai/prompts';

const SAFE_RESERVATION_HOURS = { days: [], start: null, end: null };
const CUSTOMER_OPTIONS = [
  { id: 'premium', name: 'Premium Members' },
  { id: 'followers', name: 'Followers' },
];

const addDays = (days = 0) => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + Number(days || 0));
  return d;
};

const AdvancedSettingsFields = ({ isBasicUser, businessInfo }) => {
  const { values } = useFormState();
  const form = useForm();
  const dispatch = useDispatch();

  // Ensure redemptionType has a default value - run only once on mount
  React.useEffect(() => {
    const currentValue = form.getState().values.redemptionType;   

    if (!currentValue) {
      // console.log('Setting redemptionType to auto');
      form.batch(() => {
        form.change('redemptionType', 'auto');
      });
    }
  }, []);

  // Logic to determine coupon type for display logic
  const couponTypeKey = values.couponType || 'percentage';
  const shouldShowAvailability = useMemo(() => {
    const raw = (values.goalKey || values.goal || '').toString().toLowerCase();
    if (!raw) return false;
    return raw.includes('slow') || raw.includes('off-peak') || raw.includes('repeat');
  }, [values.goalKey, values.goal]);

  // AI Terms Generation Logic
  const handleGenerateTerms = async () => {
    try {
      const prompt = termsPrompt({
        businessName: values.businessName || businessInfo?.name || 'Your Business',
        location: values.city || 'your location',
        itemName: values.itemName || values.title || 'the item',
        discountValue: values.discountValue,
        couponType: values.couponType,
        expiryType: values.expiryType,
        expiryDate: values.expiryDate,
        rules: 'Write clear terms in plain English.'
      });

      const res = await dispatchAsAsync(
        aiActions.generateText('terms', { prompt, type: 'terms_of_service', length: 600 }),
        dispatch,
      );

      if (res?.text) {
        let text = res.text.trim();
        if (text.startsWith('"')) text = text.slice(1, -1);
        form.change('terms', text);
      }
    } catch (e) {
      console.error('Terms gen failed', e);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', marginTop: '2rem', borderTop: '1px solid #eee', paddingTop: '2rem' }}>

      {/* --- EXCLUSIVITY --- */}
      <div>
        <h3 className="text-lg font-semibold mb-4 text-gray-900">Exclusivity</h3>
        <p className="text-sm text-gray-600 mb-3">
          Make this coupon exclusive to specific customer groups.
        </p>

        <Field name="customerType">
          {({ input }) => {
            const current = Array.isArray(input.value) ? input.value : [];

            const isOn = (key) => current.includes(key);

            const toggle = (key) => {
              const next = isOn(key)
                ? current.filter(v => v !== key)
                : [...current, key];

              input.onChange(next);

              // Keep exclusiveTo in sync (legacy string field)
              const preferred = next.includes('premium')
                ? 'premium'
                : next.includes('followers')
                ? 'followers'
                : '';
              form.change('exclusiveTo', preferred);
            };

            const pillStyle = (active) => ({
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '10px 14px',
              borderRadius: 9999,
              border: '1px solid #d1d5db',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              userSelect: 'none',
              transition: 'all .15s ease',
              background: active ? '#111827' : '#ffffff',
              color: active ? '#ffffff' : '#111827',
            });

            return (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  aria-pressed={isOn('premium')}
                  onClick={() => toggle('premium')}
                  style={pillStyle(isOn('premium'))}
                >
                  Premium Members
                </button>

                <button
                  type="button"
                  aria-pressed={isOn('followers')}
                  onClick={() => toggle('followers')}
                  style={pillStyle(isOn('followers'))}
                >
                  Followers
                </button>
              </div>
            );
          }}
        </Field>

        <p className="text-xs text-gray-500 mt-2">
          Leave both off to allow all customers.
        </p>
      </div>

      {/* --- RESERVATION HOURS --- */}
      <div>
        <label className="block font-semibold mb-1 text-gray-800">Reserved Hours</label>
        <Field name="reservationHours">
          {({ input }) => (
            <input
              {...input}
              type="number"
              step="0.25"
              min="0"
              placeholder="e.g., 4.00"
              className="w-full border border-gray-300 rounded-lg p-2.5"
            />
          )}
        </Field>
        <p className="text-xs text-gray-500 mt-1">
          Number of hours this coupon stays "reserved" after a customer claims it.
        </p>
      </div>

      {/* --- PER USER LIMITS --- */}
      <div>
        <h3 className="text-lg font-semibold mb-4 text-gray-900">Per User Limit</h3>
        <Field name="perUserLimitType">
          {({ input }) => (
            <FormRadio
              label="Available Per User"
              name={input.name}
              value={input.value}
              options={[
                { label: 'Unlimited', value: 'unlimited' },
                { label: 'One Time', value: 'oneTime' },
                { label: 'Multiple Times', value: 'multiple' },
              ]}
              onChange={(val) => input.onChange(val)}
            />
          )}
        </Field>

        {values.perUserLimitType === 'multiple' && (
          <Field name="perUserLimitCount">
            {({ input }) => (
              <FormField
                label="Max per User"
                type="number"
                min={2}
                max={10}
                input={input}
                meta={{}}
              />
            )}
          </Field>
        )}
      </div>

      {/* --- REDEMPTION --- */}
      <div>
        <h3 className="text-lg font-semibold mb-4 text-gray-900">Redemption Settings</h3>
        <Field name="redemptionType">
          {({ input }) => (
            <FormRadio
              label="Redemption Type"
              name={input.name}
              value={input.value}
              options={[
                { label: 'Show in-app coupon (Manual Acceptance)', value: 'manual' },
                { label: 'Auto accept coupon', value: 'auto' },
              ]}
              onChange={(val) => input.onChange(val)}
            />
          )}
        </Field>
      </div>

      {/* --- AVAILABILITY (Optional) --- */}
      {shouldShowAvailability && (
        <div>
          <h3 className="text-lg font-semibold mb-4 text-gray-900">Availability</h3>
          <Field name="availability">
            {({ input }) => (
              <TimeRangeSelector
                label="Choose days & times when this coupon is valid"
                value={input.value || SAFE_RESERVATION_HOURS}
                onChange={(range) => input.onChange(range)}
              />
            )}
          </Field>
        </div>
      )}

      {/* --- POINTS --- */}
      <div>
        <h3 className="text-lg font-semibold mb-4 text-gray-900">Points</h3>
        <Field name="redeemableWithPoints" type="checkbox">
          {({ input }) => (
            <FormSwitch
              label={
                <span>
                  Redeemable with Points{' '}
                  <FormTooltipIcon message="Allow customers to use their Coupon Points to redeem this offer." />
                </span>
              }
              checked={!!input.checked}
              onChange={(val) => input.onChange(val)}
            />
          )}
        </Field>
      </div>

    </div>
  );
};

export default AdvancedSettingsFields;

// import React, { useMemo } from 'react';
// import { Field, useFormState, useForm } from 'react-final-form';
// import {
//   FormRadio,
//   FormSwitch,
//   FormDatePicker,
//   FormField,
//   FormSelect,
//   TextArea,
//   TimeRangeSelector,
//   ExpandableSection,
//   FormTooltipIcon,
//   Button
// } from 'components';
// import { useSelector, useDispatch } from 'react-redux';
// import { actions as aiActions } from 'coupon-common/src/modules/aiContent';
// import { dispatchAsAsync } from 'coupon-common/src/utils';
// import { termsPrompt } from '../../coupon-ai/prompts';
// import { Lock } from 'react-feather';

// const SAFE_RESERVATION_HOURS = { days: [], start: null, end: null };
// const CUSTOMER_OPTIONS = [
//   { id: 'premium', name: 'Premium Members' },
//   { id: 'followers', name: 'Followers' },
// ];

// const addDays = (days = 0) => {
//   const d = new Date();
//   d.setHours(0, 0, 0, 0);
//   d.setDate(d.getDate() + Number(days || 0));
//   return d;
// };

// const AdvancedSettingsFields = ({ isBasicUser, businessInfo }) => {
//   const { values } = useFormState();
//   const form = useForm();
//   const dispatch = useDispatch();

//   // Ensure redemptionType has a default value - run only once on mount
//   React.useEffect(() => {
//     const currentValue = form.getState().values.redemptionType;
//     console.log('AdvancedSettings mounted, redemptionType:', currentValue);
//     console.log('Full form values:', form.getState().values);

//     if (!currentValue) {
//       console.log('Setting redemptionType to auto');
//       form.batch(() => {
//         form.change('redemptionType', 'auto');
//       });
//     }
//   }, []);

//   // Logic to determine coupon type for display logic
//   const couponTypeKey = values.couponType || 'percentage';
//   const shouldShowAvailability = useMemo(() => {
//     const raw = (values.goalKey || values.goal || '').toString().toLowerCase();
//     if (!raw) return false;
//     return raw.includes('slow') || raw.includes('off-peak') || raw.includes('repeat');
//   }, [values.goalKey, values.goal]);

//   // AI Terms Generation Logic
//   const handleGenerateTerms = async () => {
//     try {
//       const prompt = termsPrompt({
//         businessName: values.businessName || businessInfo?.name || 'Your Business',
//         location: values.city || 'your location',
//         itemName: values.itemName || values.title || 'the item',
//         discountValue: values.discountValue,
//         couponType: values.couponType,
//         expiryType: values.expiryType,
//         expiryDate: values.expiryDate,
//         rules: 'Write clear terms in plain English.'
//       });

//       const res = await dispatchAsAsync(
//         aiActions.generateText('terms', { prompt, type: 'terms_of_service', length: 600 }),
//         dispatch,
//       );

//       if (res?.text) {
//         let text = res.text.trim();
//         if (text.startsWith('"')) text = text.slice(1, -1);
//         form.change('terms', text);
//       }
//     } catch (e) {
//       console.error('Terms gen failed', e);
//     }
//   };

//   return (
//     <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', marginTop: '2rem', borderTop: '1px solid #eee', paddingTop: '2rem' }}>

//       {/* --- EXCLUSIVITY --- */}
//       <div>
//         <h3 className="text-lg font-semibold mb-4 text-gray-900">Exclusivity</h3>
//         <p className="text-sm text-gray-600 mb-3">
//           Make this coupon exclusive to specific customer groups.
//         </p>

//         <Field name="customerType">
//           {({ input }) => {
//             const current = Array.isArray(input.value) ? input.value : [];

//             const isOn = (key) => current.includes(key);

//             const toggle = (key) => {
//               const next = isOn(key)
//                 ? current.filter(v => v !== key)
//                 : [...current, key];

//               input.onChange(next);

//               // Keep exclusiveTo in sync (legacy string field)
//               const preferred = next.includes('premium')
//                 ? 'premium'
//                 : next.includes('followers')
//                 ? 'followers'
//                 : '';
//               form.change('exclusiveTo', preferred);
//             };

//             const pillStyle = (active) => ({
//               display: 'inline-flex',
//               alignItems: 'center',
//               justifyContent: 'center',
//               padding: '10px 14px',
//               borderRadius: 9999,
//               border: '1px solid #d1d5db',
//               fontSize: 14,
//               fontWeight: 600,
//               cursor: 'pointer',
//               userSelect: 'none',
//               transition: 'all .15s ease',
//               background: active ? '#111827' : '#ffffff',
//               color: active ? '#ffffff' : '#111827',
//             });

//             return (
//               <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
//                 <button
//                   type="button"
//                   aria-pressed={isOn('premium')}
//                   onClick={() => toggle('premium')}
//                   style={pillStyle(isOn('premium'))}
//                 >
//                   Premium Members
//                 </button>

//                 <button
//                   type="button"
//                   aria-pressed={isOn('followers')}
//                   onClick={() => toggle('followers')}
//                   style={pillStyle(isOn('followers'))}
//                 >
//                   Followers
//                 </button>
//               </div>
//             );
//           }}
//         </Field>

//         <p className="text-xs text-gray-500 mt-2">
//           Leave both off to allow all customers.
//         </p>
//       </div>

//       {/* --- RESERVATION HOURS --- */}
//       <div>
//         <label className="block font-semibold mb-1 text-gray-800">Reserved Hours</label>
//         <Field name="reservationHours">
//           {({ input }) => (
//             <input
//               {...input}
//               type="number"
//               step="0.25"
//               min="0"
//               placeholder="e.g., 4.00"
//               className="w-full border border-gray-300 rounded-lg p-2.5"
//             />
//           )}
//         </Field>
//         <p className="text-xs text-gray-500 mt-1">
//           Number of hours this coupon stays "reserved" after a customer claims it.
//         </p>
//       </div>

//       {/* --- PER USER LIMITS --- */}
//       <div>
//         <h3 className="text-lg font-semibold mb-4 text-gray-900">Per User Limit</h3>
//         <Field name="perUserLimitType">
//           {({ input }) => (
//             <FormRadio
//               label="Available Per User"
//               name={input.name}
//               value={input.value}
//               options={[
//                 { label: 'Unlimited', value: 'unlimited' },
//                 { label: 'One Time', value: 'oneTime' },
//                 { label: 'Multiple Times', value: 'multiple' },
//               ]}
//               onChange={(val) => input.onChange(val)}
//             />
//           )}
//         </Field>

//         {values.perUserLimitType === 'multiple' && (
//           <Field name="perUserLimitCount">
//             {({ input }) => (
//               <FormField
//                 label="Max per User"
//                 type="number"
//                 min={2}
//                 max={10}
//                 input={input}
//                 meta={{}}
//               />
//             )}
//           </Field>
//         )}
//       </div>

//       {/* --- REDEMPTION --- */}
//       <div>
//         <h3 className="text-lg font-semibold mb-4 text-gray-900">Redemption Settings</h3>
//         <Field name="redemptionType">
//           {({ input }) => (
//             <FormRadio
//               label="Redemption Type"
//               name={input.name}
//               value={input.value}
//               options={[
//                 { label: 'Show in-app coupon (Manual Acceptance)', value: 'manual' },
//                 { label: 'Auto accept coupon', value: 'auto' },
//               ]}
//               onChange={(val) => input.onChange(val)}
//             />
//           )}
//         </Field>
//       </div>

//       {/* --- AVAILABILITY (Optional) --- */}
//       {shouldShowAvailability && (
//         <div>
//           <h3 className="text-lg font-semibold mb-4 text-gray-900">Availability</h3>
//           <Field name="availability">
//             {({ input }) => (
//               <TimeRangeSelector
//                 label="Choose days & times when this coupon is valid"
//                 value={input.value || SAFE_RESERVATION_HOURS}
//                 onChange={(range) => input.onChange(range)}
//               />
//             )}
//           </Field>
//         </div>
//       )}

//       {/* --- POINTS --- */}
//       <div>
//         <h3 className="text-lg font-semibold mb-4 text-gray-900">Points</h3>
//         <Field name="redeemableWithPoints" type="checkbox">
//           {({ input }) => (
//             <FormSwitch
//               label={
//                 <span>
//                   Redeemable with Points{' '}
//                   <FormTooltipIcon message="Allow customers to use their Coupon Points to redeem this offer." />
//                 </span>
//               }
//               checked={!!input.checked}
//               onChange={(val) => input.onChange(val)}
//             />
//           )}
//         </Field>
//       </div>

//     </div>
//   );
// };

// export default AdvancedSettingsFields;

// // import React, { useMemo } from 'react';
// // import { Field, useFormState, useForm } from 'react-final-form';
// // import {
// //   FormRadio,
// //   FormSwitch,
// //   FormDatePicker,
// //   FormField,
// //   FormSelect,
// //   TextArea,
// //   TimeRangeSelector,
// //   ExpandableSection,
// //   FormTooltipIcon,
// //   Button
// // } from 'components'; // Assuming these export correctly or you adjust paths
// // import { useSelector, useDispatch } from 'react-redux';
// // import { actions as aiActions } from 'coupon-common/src/modules/aiContent'; // Adjust path if needed
// // import { dispatchAsAsync } from 'coupon-common/src/utils'; // Adjust path if needed
// // import { termsPrompt } from '../../coupon-ai/prompts'; // Adjust path if needed

// // const SAFE_RESERVATION_HOURS = { days: [], start: null, end: null };
// // const CUSTOMER_OPTIONS = [
// //   { id: 'premium', name: 'Premium Members' },
// //   { id: 'followers', name: 'Followers' },
// // ];

// // const addDays = (days = 0) => {
// //   const d = new Date();
// //   d.setHours(0, 0, 0, 0);
// //   d.setDate(d.getDate() + Number(days || 0));
// //   return d;
// // };

// // const AdvancedSettingsFields = ({ isBasicUser, businessInfo }) => {
// //   const { values } = useFormState();
// //   const form = useForm();
// //   const dispatch = useDispatch();

// //   // Ensure redemptionType has a default value - run only once on mount
// // React.useEffect(() => {
// //   const currentValue = form.getState().values.redemptionType;
// //   console.log('AdvancedSettings mounted, redemptionType:', currentValue);
// //   console.log('Full form values:', form.getState().values);
  
// //   if (!currentValue) {
// //     console.log('Setting redemptionType to auto');
// //     form.batch(() => {
// //       form.change('redemptionType', 'auto');
// //     });
// //   }
// // }, []);
// //   // Logic to determine coupon type for display logic
// //   const couponTypeKey = values.couponType || 'percentage';
// //   const shouldShowAvailability = useMemo(() => {
// //     const raw = (values.goalKey || values.goal || '').toString().toLowerCase();
// //     if (!raw) return false;
// //     return raw.includes('slow') || raw.includes('off-peak') || raw.includes('repeat');
// //   }, [values.goalKey, values.goal]);

// //   // AI Terms Generation Logic
// //   const handleGenerateTerms = async () => {
// //     try {
// //       const prompt = termsPrompt({
// //         businessName: values.businessName || businessInfo?.name || 'Your Business',
// //         location: values.city || 'your location',
// //         itemName: values.itemName || values.title || 'the item',
// //         discountValue: values.discountValue,
// //         couponType: values.couponType,
// //         expiryType: values.expiryType,
// //         expiryDate: values.expiryDate,
// //         rules: 'Write clear terms in plain English.'
// //       });

// //       const res = await dispatchAsAsync(
// //         aiActions.generateText('terms', { prompt, type: 'terms_of_service', length: 600 }),
// //         dispatch,
// //       );

// //       if (res?.text) {
// //         // Remove quotes and update form
// //         let text = res.text.trim();
// //         if (text.startsWith('"')) text = text.slice(1, -1);
// //         form.change('terms', text);
// //       }
// //     } catch (e) {
// //       console.error('Terms gen failed', e);
// //     }
// //   };

// //   return (
// //     <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', marginTop: '2rem', borderTop: '1px solid #eee', paddingTop: '2rem' }}>
      


// //       {/* --- RESERVATION HOURS --- */}
// //       <div>
// //         <label className="block font-semibold mb-1 text-gray-800">Reserved Hours</label>
// //         <Field name="reservationHours">
// //             {({ input }) => (
// //                 <input
// //                     {...input}
// //                     type="number"
// //                     step="0.25"
// //                     min="0"
// //                     placeholder="e.g., 4.00"
// //                     className="w-full border border-gray-300 rounded-lg p-2.5"
// //                 />
// //             )}
// //         </Field>
// //         <p className="text-xs text-gray-500 mt-1">
// //             Number of hours this coupon stays “reserved” after a customer claims it.
// //         </p>
// //       </div>

// //       {/* --- PER USER LIMITS --- */}
// //       <div>
// //         <h3 className="text-lg font-semibold mb-4 text-gray-900">Per User Limit</h3>
// //         <Field name="perUserLimitType">
// //             {({ input }) => (
// //                 <FormRadio
// //                     label="Available Per User"
// //                     name={input.name}
// //                     value={input.value}
// //                     options={[
// //                         { label: 'Unlimited', value: 'unlimited' },
// //                         { label: 'One Time', value: 'oneTime' },
// //                         { label: 'Multiple Times', value: 'multiple' },
// //                     ]}
// //                     onChange={(val) => input.onChange(val)}
// //                 />
// //             )}
// //         </Field>

// //         {values.perUserLimitType === 'multiple' && (
// //           <Field name="perUserLimitCount">
// //             {({ input }) => (
// //                 <FormField
// //                     label="Max per User"
// //                     type="number"
// //                     min={2}
// //                     max={10}
// //                     input={input}
// //                     meta={{}}
// //                 />
// //             )}
// //           </Field>
// //         )}
// //       </div>

// //       {/* --- REDEMPTION --- */}
// //       <div>
// //         <h3 className="text-lg font-semibold mb-4 text-gray-900">Redemption Settings</h3>
// //         <Field name="redemptionType">
// //             {({ input }) => (
// //                 <FormRadio
// //                     label="Redemption Type"
// //                     name={input.name}
// //                     value={input.value}
// //                     options={[
// //                         { label: 'Show in-app coupon (Manual Acceptance)', value: 'manual' },
// //                         { label: 'Auto accept coupon', value: 'auto' },
// //                     ]}
// //                     onChange={(val) => input.onChange(val)}
// //                 />
// //             )}
// //         </Field>
// //       </div>

// //       {/* --- AVAILABILITY (Optional) --- */}
// //       {shouldShowAvailability && (
// //         <div>
// //            <h3 className="text-lg font-semibold mb-4 text-gray-900">Availability</h3>
// //            <Field name="availability">
// //              {({ input }) => (
// //                <TimeRangeSelector
// //                  label="Choose days & times when this coupon is valid"
// //                  value={input.value || SAFE_RESERVATION_HOURS}
// //                  onChange={(range) => input.onChange(range)}
// //                />
// //              )}
// //            </Field>
// //         </div>
// //       )}

// //       {/* --- TERMS & POINTS --- */}
// //       <div>
// //         <h3 className="text-lg font-semibold mb-4 text-gray-900">Points & Terms</h3>
// //         <Field name="redeemableWithPoints" type="checkbox">
// //             {({ input }) => (
// //                 <FormSwitch
// //                     label={
// //                         <span>
// //                             Redeemable with Points{' '}
// //                             <FormTooltipIcon message="Allow customers to use their Coupon Points to redeem this offer." />
// //                         </span>
// //                     }
// //                     checked={!!input.checked}
// //                     onChange={(val) => input.onChange(val)}
// //                 />
// //             )}
// //         </Field>

// //         <div className="mt-4 grid grid-cols-[1fr_auto] gap-3 items-end">
// //             <Field name="terms">
// //                 {({ input }) => (
// //                     <TextArea
// //                         {...input}
// //                         label="Terms and Conditions"
// //                         placeholder="e.g., 'Valid for dine-in only.'"
// //                         maxLength={600}
// //                     />
// //                 )}
// //             </Field>
// //             <Button
// //                 primary={false}
// //                 type="button"
// //                 onClick={handleGenerateTerms}
// //                 className="mb-1"
// //             >
// //                 Generate Terms
// //             </Button>
// //         </div>
// //       </div>

// //       {/* --- EXTRAS --- */}
// //       <ExpandableSection title="More Settings (optional)">
// //          <Field name="customerType">
// //             {({ input }) => (
// //                 <FormSelect
// //                     label="Target Customer Segment"
// //                     isMulti
// //                     options={CUSTOMER_OPTIONS}
// //                     input={{
// //                         value: CUSTOMER_OPTIONS.filter(o => (input.value || []).includes(o.id)),
// //                         onChange: () => {} // handled by onChangeSelection
// //                     }}
// //                     onChangeSelection={(opts) => {
// //                         const arr = Array.isArray(opts) ? opts : (opts ? [opts] : []);
// //                         input.onChange(arr.map(o => o.id));
// //                     }}
// //                     meta={{}}
// //                 />
// //             )}
// //          </Field>
// //       </ExpandableSection>

// //     </div>
// //   );
// // };

// // export default AdvancedSettingsFields;

// import React, { useMemo } from 'react';
// import { Field, useFormState, useForm } from 'react-final-form';
// import { TimeRangeSelector, FormSwitch, FormTooltipIcon } from 'components';
// import { useSelector, useDispatch } from 'react-redux';
// import { actions as aiActions } from 'coupon-common/src/modules/aiContent';
// import { dispatchAsAsync } from 'coupon-common/src/utils';
// import { termsPrompt } from '../../coupon-ai/prompts';

// const RED = '#c0392b';
// const BORDER = '#e2d8d8';

// const SAFE_RESERVATION_HOURS = { days: [], start: null, end: null };

// /* ── Stepper: text input flanked by − (left) and + (right) red buttons ────── */
// const Stepper = ({ value, onChange, min = 0, step = 1 }) => {
//   const n = parseFloat(value) || 0;
//   return (
//     <div style={{
//       display: 'flex', alignItems: 'stretch',
//       border: `1.5px solid ${BORDER}`, borderRadius: 8,
//       overflow: 'hidden', height: 42, background: '#fff',
//     }}>
//       <input
//         value={value}
//         onChange={e => onChange(e.target.value)}
//         style={{
//           flex: 1, border: 'none', padding: '0 12px',
//           fontSize: 14, color: '#111', background: 'transparent',
//           minWidth: 0,
//         }}
//       />
//       <div style={{ display: 'flex', borderLeft: `1.5px solid ${BORDER}` }}>
//         <button
//           type="button"
//           onClick={() => onChange(Math.max(min, +(n - step).toFixed(4)))}
//           style={{
//             width: 34, border: 'none', borderRight: `1px solid ${BORDER}`,
//             background: '#fff', cursor: 'pointer', color: '#888',
//             display: 'flex', alignItems: 'center', justifyContent: 'center',
//             fontSize: 16, lineHeight: 1,
//           }}
//         >−</button>
//         <button
//           type="button"
//           onClick={() => onChange(+(n + step).toFixed(4))}
//           style={{
//             width: 34, border: 'none', background: '#fff',
//             cursor: 'pointer', color: RED,
//             display: 'flex', alignItems: 'center', justifyContent: 'center',
//             fontSize: 16, lineHeight: 1,
//           }}
//         >+</button>
//       </div>
//     </div>
//   );
// };

// /* ── Pill toggle button ──────────────────────────────────────────────────────── */
// const Pill = ({ label, active, onClick }) => (
//   <button
//     type="button"
//     onClick={onClick}
//     style={{
//       padding: '7px 16px',
//       borderRadius: 20,
//       border: `1.5px solid ${active ? '#1a1a1a' : BORDER}`,
//       background: active ? '#1a1a1a' : '#fff',
//       color: active ? '#fff' : '#444',
//       fontSize: 12,
//       fontWeight: 500,
//       cursor: 'pointer',
//       whiteSpace: 'nowrap',
//       transition: 'all 0.15s',
//     }}
//   >
//     {label}
//   </button>
// );

// /* ── Main component ──────────────────────────────────────────────────────────── */
// const AdvancedSettingsFields = ({ isBasicUser, businessInfo }) => {
//   const { values } = useFormState();
//   const form = useForm();
//   const dispatch = useDispatch();

//   // Ensure redemptionType has a default
//   React.useEffect(() => {
//     const cur = form.getState().values.redemptionType;
//     if (!cur) form.change('redemptionType', 'auto');
//   }, []); // eslint-disable-line

//   const shouldShowAvailability = useMemo(() => {
//     const raw = (values.goalKey || values.goal || '').toString().toLowerCase();
//     return raw.includes('slow') || raw.includes('off-peak') || raw.includes('repeat');
//   }, [values.goalKey, values.goal]);

//   return (
//     <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

//       {/* ── Reserved Hours + Per User Limit (side by side) ───────────────────── */}
//       <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>

//         {/* Reserved Hours */}
//         <div>
//           <span style={{ fontSize: 13, fontWeight: 500, color: '#444', display: 'block', marginBottom: 8 }}>
//             Reserved Hours
//           </span>
//           <Field name="reservationHours">
//             {({ input }) => (
//               <Stepper
//                 value={input.value}
//                 onChange={v => input.onChange(v)}
//                 min={0}
//                 step={1}
//               />
//             )}
//           </Field>
//         </div>

//         {/* Per User Limit */}
//         <div>
//           <span style={{ fontSize: 13, fontWeight: 500, color: '#444', display: 'block', marginBottom: 8 }}>
//             Per User Limit
//           </span>
//           <Field name="perUserLimitType">
//             {({ input }) => (
//               <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
//                 {[
//                   { v: 'unlimited', l: 'Unlimited' },
//                   { v: 'oneTime',   l: 'One time' },
//                   { v: 'multiple',  l: 'Multiple Times' },
//                 ].map(o => (
//                   <Pill
//                     key={o.v}
//                     label={o.l}
//                     active={input.value === o.v}
//                     onClick={() => input.onChange(o.v)}
//                   />
//                 ))}
//               </div>
//             )}
//           </Field>

//           {/* Max per User — shown only when Multiple Times selected */}
//           {values.perUserLimitType === 'multiple' && (
//             <div style={{ marginTop: 14 }}>
//               <span style={{ fontSize: 13, fontWeight: 500, color: '#444', display: 'block', marginBottom: 8 }}>
//                 Max per User
//               </span>
//               <Field name="perUserLimitCount">
//                 {({ input }) => (
//                   <Stepper
//                     value={input.value}
//                     onChange={v => input.onChange(v)}
//                     min={2}
//                     step={1}
//                   />
//                 )}
//               </Field>
//             </div>
//           )}
//         </div>
//       </div>

//       {/* ── Points toggle ─────────────────────────────────────────────────────── */}
//       <div style={{ marginBottom: 20 }}>
//         <Field name="redeemableWithPoints" type="checkbox">
//           {({ input }) => (
//             <FormSwitch
//               label={
//                 <span style={{ fontSize: 13, color: '#444' }}>
//                   Redeemable with Points{' '}
//                   <FormTooltipIcon message="Allow customers to use their Coupon Points to redeem this offer." />
//                 </span>
//               }
//               checked={!!input.checked}
//               onChange={val => input.onChange(val)}
//             />
//           )}
//         </Field>
//       </div>

//       {/* ── Availability (conditional) ────────────────────────────────────────── */}
//       {shouldShowAvailability && (
//         <div style={{ marginBottom: 20 }}>
//           <span style={{ fontSize: 13, fontWeight: 600, color: '#111', display: 'block', marginBottom: 10 }}>
//             Availability
//           </span>
//           <Field name="availability">
//             {({ input }) => (
//               <TimeRangeSelector
//                 label="Choose days & times when this coupon is valid"
//                 value={input.value || SAFE_RESERVATION_HOURS}
//                 onChange={range => input.onChange(range)}
//               />
//             )}
//           </Field>
//         </div>
//       )}

//     </div>
//   );
// };

// export default AdvancedSettingsFields;